/**
 * Fase 1.3 — Página de gerenciamento de conexões de calendário (multi-conta).
 *
 * Backend endpoints:
 *   GET    /api/calendar/connections
 *   POST   /api/calendar/connections  → { authorization_url, intent:"add" }
 *   PATCH  /api/calendar/connections/{identifier}  body: { label }
 *   DELETE /api/calendar/connections/{identifier}
 *
 * Cap de 5 conexões — botão "Adicionar conexão" desabilita em 5/5.
 *
 * URL params interpretados (vindos do callback OAuth):
 *   ?google_added=true&email=...    → toast de sucesso (Google)
 *   ?google_error=...               → toast de erro (Google)
 *   ?outlook_added=true&email=...   → toast de sucesso (Outlook)
 *   ?outlook_error=...              → toast de erro (Outlook)
 *
 * Fase 2.2: botão "Adicionar conexão" abre dropdown com 2 opções
 * (Google + Outlook). Cada provider tem endpoint próprio de início OAuth.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Calendar as CalendarIcon, Plus, Pencil, Trash2, RefreshCw, MoreVertical, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import { MainLayout } from "@/components/layout";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

function StatusBadge({ status }) {
  const map = {
    active: { label: "Ativa", className: "bg-status-success/10 text-status-success border-status-success/30" },
    expired: { label: "Expirada", className: "bg-status-warning/10 text-status-warning border-status-warning/30" },
    revoked: { label: "Revogada", className: "bg-foreground-muted/10 text-foreground-muted border-foreground-muted/30" },
    error: { label: "Erro", className: "bg-status-error/10 text-status-error border-status-error/30" },
  };
  const cfg = map[status] || map.active;
  return (
    <Badge variant="outline" className={`text-caption ${cfg.className}`}>
      {cfg.label}
    </Badge>
  );
}

export default function CalendarConnectionsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [conns, setConns] = useState([]);
  const [maxConns, setMaxConns] = useState(5);
  const [connectedLegacy, setConnectedLegacy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null); // {identifier, label}

  const fetchConns = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/calendar/connections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setConns(data.connections || []);
      setMaxConns(data.max || 5);
      setConnectedLegacy(!!data.connected_legacy);
    } catch (e) {
      console.error("[conexoes] fetch falhou:", e);
      toast.error("Não consegui carregar suas conexões. Recarrega a página.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchConns();
  }, [fetchConns]);

  // Toast pra ?google_added=true|google_error= vindo do callback OAuth.
  // Limpa o param da URL pra não retoastrar em refresh.
  const toastedRef = useRef(false);
  useEffect(() => {
    if (toastedRef.current) return;
    const googleAdded = params.get("google_added");
    const googleErr = params.get("google_error");
    const outlookAdded = params.get("outlook_added");
    const outlookErr = params.get("outlook_error");
    const email = params.get("email");
    if (googleAdded === "true" || outlookAdded === "true") {
      toastedRef.current = true;
      toast.success(`Conta ${email || "adicionada"}. Renomeie à vontade.`);
      setParams({}, { replace: true });
    } else if (googleErr || outlookErr) {
      toastedRef.current = true;
      toast.error(`Não rolou: ${decodeURIComponent(googleErr || outlookErr)}`);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  // Fase 2.2: provider = "google" | "outlook". Cada um tem endpoint de
  // start próprio. Google: POST /api/calendar/connections.
  // Outlook: GET /api/auth/outlook/start.
  const handleAdd = async (provider = "google") => {
    if (conns.length >= maxConns) return;
    try {
      let r;
      if (provider === "outlook") {
        r = await fetch(`${API_URL}/api/auth/outlook/start`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        r = await fetch(`${API_URL}/api/calendar/connections`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        const msg = j?.detail?.message || j?.detail || `Erro ${r.status}`;
        toast.error(msg);
        return;
      }
      const data = await r.json();
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (e) {
      console.error("[conexoes] add falhou:", e);
      toast.error("Não consegui iniciar a conexão. Tenta de novo.");
    }
  };

  const handleSaveLabel = async (identifier, newLabel) => {
    const trimmed = (newLabel || "").trim();
    if (!trimmed) {
      toast.error("O nome não pode ficar vazio.");
      return;
    }
    if (trimmed.length > 40) {
      toast.error("Use até 40 caracteres.");
      return;
    }
    try {
      const r = await fetch(
        `${API_URL}/api/calendar/connections/${encodeURIComponent(identifier)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ label: trimmed }),
        },
      );
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j?.detail || "Não consegui renomear.");
        return;
      }
      setEditingId(null);
      await fetchConns();
      toast.success("Renomeado.");
    } catch (e) {
      console.error("[conexoes] rename falhou:", e);
      toast.error("Falha ao renomear. Tenta de novo.");
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      const r = await fetch(
        `${API_URL}/api/calendar/connections/${encodeURIComponent(confirmDelete.identifier)}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!r.ok) {
        toast.error("Não consegui desconectar.");
        return;
      }
      setConfirmDelete(null);
      await fetchConns();
      toast.success("Conta desconectada.");
    } catch (e) {
      console.error("[conexoes] delete falhou:", e);
      toast.error("Falha ao desconectar.");
    }
  };

  const handleReconnect = async () => {
    // Reconexão é mesmo path do POST (state=add). Backend já trata como
    // update se account_email bater no callback (save_connection_tokens
    // faz upsert).
    await handleAdd();
  };

  const capReached = conns.length >= maxConns;

  return (
    <MainLayout
      activeSection="settings"
      onSectionChange={(id) => navigate(`/#${id}`)}
      showContextPanel={false}
      confirmations={[]}
      messages={[]}
    >
      <div className="p-6 md:p-8 max-w-3xl mx-auto" data-testid="calendar-connections-page">
        <PageHeader
          icon={CalendarIcon}
          title="Conexões de calendário"
          subtitle={`Você tem ${conns.length} de ${maxConns} conexões.`}
          actions={(
            <Link
              to="/settings"
              className="text-caption text-foreground-muted hover:text-foreground inline-flex items-center gap-1"
            >
              <SettingsIcon className="w-4 h-4" /> Configurações
            </Link>
          )}
        />

        {loading ? (
          <div className="p-8 text-center text-foreground-muted">Carregando...</div>
        ) : conns.length === 0 ? (
          <EmptyState
            connectedLegacy={connectedLegacy}
            onAdd={handleAdd}
          />
        ) : (
          <div className="space-y-3 mt-4">
            {conns.map((c) => (
              <ConnectionCard
                key={c.identifier}
                conn={c}
                isEditing={editingId === c.identifier}
                editValue={editValue}
                onStartEdit={() => {
                  setEditingId(c.identifier);
                  setEditValue(c.label);
                }}
                onChangeEdit={setEditValue}
                onSaveEdit={() => handleSaveLabel(c.identifier, editValue)}
                onCancelEdit={() => setEditingId(null)}
                onReconnect={handleReconnect}
                onDelete={() => setConfirmDelete({ identifier: c.identifier, label: c.label, provider: c.provider })}
              />
            ))}
          </div>
        )}

        <div className="mt-6">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                disabled={capReached}
                data-testid="btn-add-connection"
                title={capReached ? `Limite de ${maxConns} conexões atingido` : undefined}
                aria-label="Adicionar conexão"
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Adicionar conexão
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                onClick={() => handleAdd("google")}
                data-testid="btn-add-google"
              >
                <CalendarIcon className="w-4 h-4 mr-2 text-brand-cyan" />
                Google Calendar
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleAdd("outlook")}
                data-testid="btn-add-outlook"
              >
                <CalendarIcon className="w-4 h-4 mr-2 text-brand-purple" />
                Outlook / Microsoft 365
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {capReached && (
            <p className="text-caption text-foreground-muted mt-2">
              Você está no limite de {maxConns} agendas. Pra adicionar outra, desconecte uma primeiro.
            </p>
          )}
        </div>

        {/* Modal confirmação de desconexão. Texto provider-aware:
            Microsoft NÃO tem endpoint de revoke programático, então a
            UI orienta o user a revogar manual em account.live.com. */}
        <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Desconectar {confirmDelete?.label}?</DialogTitle>
              <DialogDescription>
                {confirmDelete?.provider === "outlook" ? (
                  <>
                    Eventos criados nessa agenda continuam no seu Outlook — só a
                    sincronização com o Nexo para. Pra revogar 100% o acesso do
                    Nexo, abra{" "}
                    <a
                      href="https://account.live.com/consent/Manage"
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand-cyan underline"
                    >
                      account.live.com/consent/Manage
                    </a>{" "}
                    e remova o app "Nexo" da lista.
                  </>
                ) : (
                  <>
                    Eventos criados nessa agenda continuam no seu Google Calendar — só a sincronização com o Nexo para.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmDelete(null)} data-testid="btn-cancel-disconnect">
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                data-testid="btn-confirm-disconnect"
              >
                Desconectar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}


function EmptyState({ connectedLegacy, onAdd }) {
  return (
    <div className="mt-6 p-10 rounded-xl bg-surface-card border border-subtle text-center">
      <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-foreground-muted" />
      {connectedLegacy ? (
        <>
          <h3 className="text-h3 text-foreground mb-2">Conexão antiga detectada</h3>
          <p className="text-body text-foreground-muted mb-5">
            Sua conexão atual está num formato antigo. Reconecte pra usar o modelo multi-conta.
          </p>
        </>
      ) : (
        <>
          <h3 className="text-h3 text-foreground mb-2">Nenhuma agenda conectada</h3>
          <p className="text-body text-foreground-muted mb-5">
            Conecte uma agenda Google ou Outlook pra o Nexo agendar por você.
          </p>
        </>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="gap-2" data-testid="btn-empty-add">
            <Plus className="w-4 h-4" />
            Conectar agenda
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          <DropdownMenuItem onClick={() => onAdd("google")} data-testid="btn-empty-add-google">
            <CalendarIcon className="w-4 h-4 mr-2 text-brand-cyan" />
            Google Calendar
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onAdd("outlook")} data-testid="btn-empty-add-outlook">
            <CalendarIcon className="w-4 h-4 mr-2 text-brand-purple" />
            Outlook / Microsoft 365
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}


function ConnectionCard({
  conn, isEditing, editValue, onStartEdit, onChangeEdit, onSaveEdit, onCancelEdit,
  onReconnect, onDelete,
}) {
  // Fase 2.4 polish — cor por provider. Google cyan, Outlook purple
  // (mesma paleta usada no DropdownMenu de Adicionar).
  const isOutlook = conn.provider === "outlook";
  const iconBg = isOutlook ? "bg-brand-purple/10" : "bg-brand-cyan/10";
  const iconBorder = isOutlook ? "border-brand-purple/30" : "border-brand-cyan/30";
  const iconColor = isOutlook ? "text-brand-purple" : "text-brand-cyan";
  const providerLabel = isOutlook ? "Outlook" : "Google";

  return (
    <div
      className="p-4 rounded-xl bg-surface-card border border-subtle flex items-center gap-4"
      data-testid={`connection-card-${conn.identifier}`}
      data-provider={conn.provider}
    >
      {/* Ícone provider-aware */}
      <div
        className={`w-10 h-10 rounded-lg ${iconBg} border ${iconBorder} flex items-center justify-center shrink-0`}
        aria-label={`Ícone ${providerLabel}`}
        title={providerLabel}
      >
        <CalendarIcon className={`w-5 h-5 ${iconColor}`} />
      </div>

      {/* Label + email */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Input
              autoFocus
              value={editValue}
              onChange={(e) => onChangeEdit(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSaveEdit();
                if (e.key === "Escape") onCancelEdit();
              }}
              maxLength={40}
              className="max-w-xs"
              data-testid={`label-input-${conn.identifier}`}
            />
            <Button size="sm" onClick={onSaveEdit} data-testid={`btn-save-label-${conn.identifier}`}>Salvar</Button>
            <Button size="sm" variant="ghost" onClick={onCancelEdit}>Cancelar</Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onStartEdit}
            className="text-body text-foreground hover:text-brand-cyan transition-colors text-left"
            data-testid={`label-display-${conn.identifier}`}
            aria-label="Renomear conexão"
          >
            {conn.label}
            <Pencil className="inline-block w-3 h-3 ml-2 opacity-50" />
          </button>
        )}
        <p className="text-caption text-foreground-muted truncate">{conn.account_email}</p>
      </div>

      {/* Status badge */}
      <StatusBadge status={conn.status} />

      {/* Menu 3-dots */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Mais opções"
            data-testid={`menu-trigger-${conn.identifier}`}
          >
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onStartEdit}>
            <Pencil className="w-4 h-4 mr-2" />
            Renomear
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onReconnect}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reconectar
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={onDelete}
            className="text-status-error focus:text-status-error"
            data-testid={`menu-disconnect-${conn.identifier}`}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Desconectar
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
